/** @jsxImportSource @compiled/react */
import React, { type FC, useCallback, useEffect, useRef } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

// Jest uses classic JSX (`React.createElement`); TS `noUnusedLocals` does not treat JSX as reading `React`.
void React;

import FeedbackCollector from '@atlaskit/feedback-collector';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { RovoIcon } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';

import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const i18n = defineMessages({
	feedbackButtonLabel: {
		id: 'space-overview.smart-space-overview.feedback-button-label',
		defaultMessage: 'Give feedback',
		description:
			'Label for the button shown above the smart space overview cover picture to open the standard feedback dialog.',
	},
	feedbackTitle: {
		id: 'space-overview.smart-space-overview.feedback-title',
		defaultMessage: 'What do you think of the new Spaces overview?',
		description: 'Title shown in the feedback dialog for the smart space overview experience.',
	},
	feedbackPreamble: {
		id: 'space-overview.smart-space-overview.feedback-preamble',
		defaultMessage: 'Help make it even better with your feedback.',
		description: 'Preamble shown in the feedback dialog for the smart space overview experience.',
	},
	curatedByRovo: {
		id: 'space-overview.smart-space-overview.curated-by-rovo',
		defaultMessage: 'Curated by Rovo',
		description:
			'Label shown in the space overview header indicating that the content is curated by Rovo AI.',
	},
});

const FEEDBACK_OPENED_STORAGE_KEY = 'smart-space-overview-feedback-opened';
const FEEDBACK_CONTEXT_FIELD_ID = 'customfield_10047';
const FEEDBACK_ENTRYPOINT_ID = '6b7a459d-eab0-4b18-b825-481b90b7a262';
const FEEDBACK_SOURCE = 'smartSpaceOverviewHeader';

const hasFeedbackCollectorBeenOpened = () => {
	try {
		return localStorage.getItem(FEEDBACK_OPENED_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
};

const persistFeedbackCollectorOpened = () => {
	try {
		localStorage.setItem(FEEDBACK_OPENED_STORAGE_KEY, 'true');
	} catch {
		// ignore
	}
};

export const SmartSpaceOverviewHeader: FC = () => {
	const { formatMessage, locale } = useIntl();
	const { displayName, userId } = useSessionData();
	const autoOpenTimeoutRef = useRef<number | null>(null);
	const [isFeedbackCollectorOpen, setIsFeedbackCollectorOpen] = React.useState(false);

	const openFeedbackCollector = useCallback(() => {
		persistFeedbackCollectorOpened();

		if (autoOpenTimeoutRef.current !== null) {
			window.clearTimeout(autoOpenTimeoutRef.current);
			autoOpenTimeoutRef.current = null;
		}

		setIsFeedbackCollectorOpen(true);
	}, []);

	const closeFeedbackCollector = useCallback(() => {
		setIsFeedbackCollectorOpen(false);
	}, []);

	useEffect(() => {
		if (expVal('confluence_space_overview_feedback_auto_open_delay', 'value', 0) <= 0) {
			return;
		}

		autoOpenTimeoutRef.current = window.setTimeout(
			() => {
				autoOpenTimeoutRef.current = null;
				if (!hasFeedbackCollectorBeenOpened()) {
					openFeedbackCollector();
				}
			},
			expVal('confluence_space_overview_feedback_auto_open_delay', 'value', 0),
		);

		return () => {
			if (autoOpenTimeoutRef.current !== null) {
				window.clearTimeout(autoOpenTimeoutRef.current);
				autoOpenTimeoutRef.current = null;
			}
		};
	}, [openFeedbackCollector]);

	return (
		<>
			<div css={styles.overviewHeaderActions}>
				<span css={styles.curatedByRovo}>
					<RovoIcon size="xsmall" appearance="brand" shouldUseNewLogoDesign />
					<FormattedMessage {...i18n.curatedByRovo} />
				</span>
				<Button
					onClick={openFeedbackCollector}
					testId="smart-space-overview-feedback-button"
					css={styles.feedbackButton}
					iconBefore={(iconProps) => <FeedbackIcon {...iconProps} spacing="spacious" label="" />}
				>
					<FormattedMessage {...i18n.feedbackButtonLabel} />
				</Button>
			</div>
			{isFeedbackCollectorOpen && (
				<FeedbackCollector
					locale={locale}
					onClose={closeFeedbackCollector}
					onSubmit={closeFeedbackCollector}
					atlassianAccountId={userId || ''}
					name={displayName || ''}
					entrypointId={FEEDBACK_ENTRYPOINT_ID}
					feedbackTitle={formatMessage(i18n.feedbackTitle)}
					feedbackTitleDetails={formatMessage(i18n.feedbackPreamble)}
					additionalFields={[
						{
							id: FEEDBACK_CONTEXT_FIELD_ID,
							value: FEEDBACK_SOURCE,
						},
					]}
				/>
			)}
		</>
	);
};

const styles = cssMap({
	overviewHeaderActions: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: token('space.250'),
		minHeight: token('space.600'),
		paddingBlock: token('space.050'),
	},
	curatedByRovo: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		font: token('font.body'),
		color: token('color.text.subtlest'),
	},
	feedbackButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		color: token('color.text'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
		cursor: 'pointer',
	},
});
